import 'react-pdf/dist/esm/Page/TextLayer.css';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import {showSnackbar, SnackbarType} from 'webapp-lib/Functions';

import {CasusReadonlyReason} from '@lexian/api/invoicing-api';
import {showCasusReadonlyWarningSnackbar} from '_/utilities/casus';

import {RequestHandlerTypes} from './api/utilities/globalRequestHandlers';
import i18n from './lib/i18n';

type ServerError = {
  response: {
    body: {
      conditionType: string;
      message: string;
      correlationId: string;
      timestamp: string;
    };
  };
};

const isServerError = (error: unknown): error is ServerError => {
  const castedError = error as ServerError;
  const response = castedError?.response?.body;
  return (
    typeof response?.correlationId === 'string' &&
    typeof response?.conditionType === 'string' &&
    typeof response?.message === 'string' &&
    typeof response?.timestamp === 'string'
  );
};

/**
 * globalRequestHandlers are statically defined global error handlers for certain cases which do not need to be handled on module basis
 */
export const globalRequestHandlers: Record<
  RequestHandlerTypes,
  (error: unknown) => unknown
> = {
  defaultErrorCondition: (error) => {
    if (!isServerError(error)) {
      showSnackbar({
        text: i18n.t('common:snackbar.error.unexpectedError'),
        type: SnackbarType.WARNING,
        override: {
          id: 'unexpectedError',
          mode: 'REPLACE',
        },
      });
      return null;
    }
    showSnackbar({
      text: i18n.t('common:snackbar.error.unexpectedError'),
      type: SnackbarType.WARNING,
      override: {
        id: `unexpectedErrorWithEmailOption`,
        mode: 'REPLACE',
      },
    });
    return null;
  },
  TranslatedCondition: (condition) => {
    const translatedCondition = condition as TranslatedCondition;
    showSnackbar({
      text: translatedCondition.translatedMessage,
      type: SnackbarType.WARNING,
      override: {
        id: translatedCondition.errorCode,
        mode: 'REPLACE',
      },
    });
    return null;
  },
  DocumentNotFoundCondition: () => {
    showSnackbar({
      text: i18n.t('common:documentNotFoundCondition'),
      type: SnackbarType.WARNING,
      override: {
        id: 'DocumentNotFoundCondition',
        mode: 'REPLACE',
      },
    });
    return null;
  },
  CasusReadonlyCondition: (condition) => {
    const casusReadonlyCondition = condition as CasusReadonlyCondition;
    showCasusReadonlyWarningSnackbar(casusReadonlyCondition.reason);
    return null;
  },
  Aborted: () => {},
};

// keep in sync with 'TranslatedCondition.kt'
type TranslatedCondition = {
  errorCode: string;
  translatedMessage: string;
};

// keep in sync with 'CasusReadonlyCondition.kt'
type CasusReadonlyCondition = {
  lockedCasusId: string;
  reason: CasusReadonlyReason;
};
